import { useQuery } from '@tanstack/react-query';
import { QueryKey } from '../../enums/QueryKey';
import dayjs, { Dayjs } from '@utils/dayjs';
import {
    getCorrectedTotals,
    getCorrectedTotalsForLocation,
    getCorrectedTotalsSummary,
    getLastCorrectedTotalsReleaseDate,
    getLastCorrectedTotalsUploadDate,
    getSumOfCorrectedTotals,
} from '../../services/correctedTotals';
import { formatLastUploadDate } from '../../utils/dateUtils';
import { CorrectedTotalSummary } from '../../types/CorrectedTotalSummary';
import { appTimezone } from '../../config';

export function getCorrectedTotalsQueryKey({
    date,
    workingUnitLocation,
    released,
}: {
    date: string | Date | Dayjs;
    workingUnitLocation: number;
    released?: boolean;
}) {
    // Use app timezone since slots represent business times in Berlin
    const dateFrom = dayjs(date).tz(appTimezone).startOf('month').toISOString();
    const dateTo = dayjs(date).tz(appTimezone).endOf('month').toISOString();
    return [QueryKey.CorrectedTotals, { dateFrom, dateTo, workingUnitLocation, released }];
}

export function useSumOfCorrectedTotals({
    date,
    workingUnitLocation,
}: {
    date: string | Dayjs;
    workingUnitLocation: number;
}) {
    // Use app timezone since slots represent business times in Berlin
    const dateFrom = dayjs(date).tz(appTimezone).startOf('month').toISOString();
    const dateTo = dayjs(date).tz(appTimezone).endOf('month').toISOString();

    const { data: sumOfCorrectedTotals } = useQuery({
        queryKey: [QueryKey.CorrectedTotalsSum, { dateFrom, dateTo, workingUnitLocation }],
        queryFn: () => getSumOfCorrectedTotals({ dateFrom, dateTo, workingUnitLocation }),
        initialData: 0,
        enabled: !!date && !!workingUnitLocation,
    });

    return { sumOfCorrectedTotals };
}

export function useLastCorrectedTotalsUploadDate(workingUnitLocation: any) {
    const { data } = useQuery({
        queryKey: [QueryKey.LastCorrectedTotalsUploadDate, { workingUnitLocation }],
        queryFn: () => getLastCorrectedTotalsUploadDate({ workingUnitLocation }),
        enabled: !!workingUnitLocation,
        refetchOnWindowFocus: false,
    });

    return data ? formatLastUploadDate(data) : null;
}

export const useCorrectedTotals = ({
    date,
    workingUnitLocation,
    released,
}: {
    date: string | Dayjs | Date;
    workingUnitLocation: number;
    released?: boolean;
}) => {
    // Use app timezone since slots represent business times in Berlin
    const dateFrom = dayjs(date).tz(appTimezone).startOf('month').toISOString();
    const dateTo = dayjs(date).tz(appTimezone).endOf('month').toISOString();

    const { data, ...rest } = useQuery({
        queryKey: getCorrectedTotalsQueryKey({ date, workingUnitLocation, released }),
        queryFn: () => getCorrectedTotals({ dateFrom, dateTo, workingUnitLocation, released }),
        enabled: !!date && !!workingUnitLocation,
        refetchOnWindowFocus: false,
    });

    return {
        data: data || [],
        ...rest,
    };
};

export const useCorrectedTotalsForLocation = ({ date, location }: { date: string | Dayjs; location?: number }) => {
    // Use app timezone since slots represent business times in Berlin
    const dateFrom = dayjs(date).tz(appTimezone).startOf('month').toISOString();
    const dateTo = dayjs(date).tz(appTimezone).endOf('month').toISOString();

    const { data } = useQuery({
        queryKey: [QueryKey.CorrectedTotals, { dateFrom, dateTo, location }],
        queryFn: () => getCorrectedTotalsForLocation({ dateFrom, dateTo, location }),
        enabled: !!date && !!location,
        initialData: [],
    });

    return [data];
};

export function useLastCorrectedTotalsReleaseDate(workingUnitLocation: any) {
    const { data } = useQuery({
        queryKey: [QueryKey.LastCorrectedTotalsReleaseDate, { workingUnitLocation }],
        queryFn: () => getLastCorrectedTotalsReleaseDate({ workingUnitLocation }),
        enabled: !!workingUnitLocation,
    });

    return data ? formatLastUploadDate(data) : null;
}

export function useCorrectedTotalsSummary({ date }: { date: string | Dayjs }) {
    // Use app timezone since slots represent business times in Berlin
    const dateFrom = dayjs(date).tz(appTimezone).startOf('month').toISOString();
    const dateTo = dayjs(date).tz(appTimezone).endOf('month').toISOString();

    const { data } = useQuery<unknown, unknown, Array<CorrectedTotalSummary>>({
        queryKey: [QueryKey.CorrectedTotalsSummary, { dateFrom, dateTo }],
        queryFn: () => getCorrectedTotalsSummary({ dateFrom, dateTo }),
        enabled: !!date,
        initialData: [],
    });

    return { data };
}
